import React from 'react'
import cn from 'classnames'
import { ArrowBigUp, Users } from 'lucide-react'
import { useBsvExchangeRate } from 'api/useBsvExchangeRate'
import { useStreamCardStats } from 'components/shared/useStreamCardStats'
import {
    cardAmount,
    formatMembersLabel,
    type CardAmount,
} from 'components/shared/streamHostEarningsDisplay'
import styles from './channel-stats.module.scss'

// Members and the money a channel moves: what people upvalued on it over the
// last 30 days and, when it has a host share, what its host earned. Shared by
// the channel header and the channel card. Each amount reads in orange sats
// under 10 cents and in dollars from there up.
export default function ChannelStats({
    team,
    className,
}: {
    team: { id: string } | null
    className?: string
}) {
    const { membersCount, upvalueSats, hostEarningsSats } = useStreamCardStats(team)
    const membersLabel = formatMembersLabel(membersCount)

    const moneyShown = upvalueSats != null || hostEarningsSats != null
    const exchangeRate = useBsvExchangeRate({ enabled: moneyShown })
    const upvalue = cardAmount(upvalueSats, exchangeRate)
    const hostEarnings = upvalue ? cardAmount(hostEarningsSats, exchangeRate) : null

    if (!membersLabel && !upvalue) return null

    return (
        <div className={cn(styles.channelStats, className)}>
            {membersLabel && (
                <span className={styles.stat}>
                    <Users size={13} aria-hidden />
                    {membersLabel}
                </span>
            )}
            {upvalue && (
                <span
                    className={styles.stat}
                    title="Upvalued on this channel over the last 30 days"
                >
                    <ArrowBigUp
                        size={14}
                        strokeWidth={2.5}
                        aria-hidden
                        className={amountClass(upvalue)}
                    />
                    <span className={cn(styles.amount, amountClass(upvalue))}>{upvalue.label}</span>
                    upvalued
                </span>
            )}
            {hostEarnings && (
                <span
                    className={styles.stat}
                    title="Paid to this channel's host over the last 30 days"
                >
                    <span className={cn(styles.amount, amountClass(hostEarnings))}>
                        {hostEarnings.label}
                    </span>
                    to host
                </span>
            )}
        </div>
    )
}

const amountClass = (amount: CardAmount) => (amount.unit === 'sats' ? styles.sats : styles.usd)
